import React from 'react';
import styled from 'styled-components';
import { Box } from '@ally/metronome-ui';
import { formattedDate, getStatus } from '../utils';
import { trackerSteps, trackerStatuses } from '../../constants';
import TimelineTrackerStep from './TimelineTrackerStep';
import SummaryTrackerStep from './SummaryTrackerStep';
const TimelineTrackerSteps = styled.ol `
  display: flex;
  text-align: center;
  align-items: start;
  margin: 0 auto;
  padding: 0;
  list-style: none;
  counter-reset: counter;

  ${({ isSummaryView, theme: { media } }) => `
    ${isSummaryView
    ? `
          justify-content: space-between;
          align-content: stretch;
        `
    : `
          ${media.down('md')} {
            display: block;
            text-align: left;
          }
        `}
  `}

  && li:nth-child(odd) {
    background: transparent;
  }
`;
function getCanceledSteps({ activityName, statuses, }) {
    if (activityName === 'DebitCardOrder') {
        return trackerSteps.DebitCardOrderCanceled;
    }
    if (statuses.length === 1) {
        return trackerSteps.CanceledSteps;
    }
    const allsteps = trackerSteps[activityName];
    const canceledAfterRequested = statuses.length === 2;
    const statusBeforeCanceled = statuses[1] && statuses[1].status;
    const stepBeforeIndex = allsteps.indexOf(statusBeforeCanceled);
    const canceledSteps = canceledAfterRequested
        ? allsteps.slice(0, 1)
        : allsteps.slice(0, stepBeforeIndex);
    return [...canceledSteps, 'Canceled'];
}
function getStepStatus({ latestStatus, step, timelineSteps, currentStepIndex, hasWarning, }) {
    const latestStepIndex = timelineSteps.indexOf(latestStatus);
    const isCurrentStep = currentStepIndex === latestStepIndex;
    const isFirstStep = currentStepIndex === 0;
    if (step === 'Canceled')
        return 'Canceled';
    if (isCurrentStep && hasWarning)
        return 'Warning';
    // Always show first step as complete, if its not a warning
    if (isFirstStep)
        return 'Success';
    // Always show last step as complete when it has been completed
    if (latestStepIndex === timelineSteps.length - 1)
        return 'Success';
    // This is the step in progress
    if (isCurrentStep)
        return 'Current';
    // Show all steps prior to inprogress step as complete
    if (currentStepIndex < latestStepIndex)
        return 'Success';
    // All steps after in progress are treated as incomplete
    return '';
}
function getAriaLabel({ event, steps, hasWarning, }) {
    const { activityName, eventModelData, statuses } = event;
    // filter out non-steps
    const statusArray = statuses.filter(status => status.status !== 'VerificationRequired');
    const currentStatus = statusArray[0].status;
    function getAriaStepStatus(step, index) {
        const { deliverOnDate } = eventModelData;
        const allSteps = trackerSteps[activityName];
        const hasDeliveryProblem = currentStatus === 'DeliveryFailed' || currentStatus === 'Delayed';
        // If debit cards have delivery problem, show on 2nd index (delivery)
        const currentStatusIndex = hasDeliveryProblem
            ? 2
            : allSteps.map(stepItem => stepItem).indexOf(currentStatus);
        const prevSteps = allSteps.slice(0, currentStatusIndex);
        const isCurrent = index === currentStatusIndex;
        const skippedStep = prevSteps.includes(step);
        const isAch = activityName === 'ACHTransfer';
        const formattedDeliverOnDate = formattedDate(deliverOnDate, 'MMM D');
        const stepContent = () => {
            if (isAch) {
                if (step === 'Completed') {
                    return 'Deliver On';
                }
                return 'Processing';
            }
            if (step === 'Posted')
                return 'Delivered';
            return step;
        };
        const historyDate = statuses.find(history => {
            return getStatus({ activityName, currentStatus: history.status }) === step;
        });
        // Debit issues didnt have either historyDate or deliverOnDate
        if (historyDate || deliverOnDate || (hasWarning && isCurrent)) {
            const historyStatus = historyDate === null || historyDate === void 0 ? void 0 : historyDate.status;
            const historyStatusDate = formattedDate(historyDate === null || historyDate === void 0 ? void 0 : historyDate.eventDateTime, 'MMM D');
            const isInProgress = (currentStatus === 'Reviewing' && historyStatus === 'Reviewing') ||
                (currentStatus === 'Processing' && historyStatus === 'Processing') ||
                (activityName === 'DebitCardOrder' &&
                    currentStatus === 'Shipped' &&
                    historyStatus === 'Shipped') ||
                (activityName === 'DebitCardOrder' &&
                    currentStatus === 'Delivered' &&
                    historyStatus === 'Delivered');
            const deliveryFailed = currentStatus === 'DeliveryFailed';
            if (isInProgress && !hasWarning) {
                return `${stepContent()} in progress on ${historyStatusDate}`;
            }
            if (hasWarning && isCurrent && !deliveryFailed) {
                return `${stepContent()} on hold ${historyStatusDate}`;
            }
            if (isAch) {
                return `${stepContent()} ${formattedDeliverOnDate}`;
            }
            return `${stepContent()} complete on ${isAch ? formattedDeliverOnDate : historyStatusDate}`;
        }
        if (skippedStep && !historyDate && !hasWarning) {
            return `${stepContent()} complete`;
        }
        return `${stepContent()}`;
    }
    return `
    ${steps.length} step process:
    ${steps
        .map((step, i) => `step ${i + 1}, ${getAriaStepStatus(step, i)}`)
        .join(', ')}
  `;
}
const TimelineTracker = ({ event, isSummaryView = false, isWiresMigrationEnabled }) => {
    const { activityName, statuses, eventModelData } = event;
    const { contactUsFlag } = eventModelData;
    // We are respecting 'Canceled', 'Delayed', 'DeliveryFailed' and 'VerificationRequired' ONLY if its the first item from statuses
    const firstItem = statuses[0].status;
    const isCanceled = firstItem === 'Canceled';
    const isDeliveryDelayed = firstItem === 'Delayed';
    const isDeliveryFailed = firstItem === 'DeliveryFailed';
    const isVerification = firstItem === 'VerificationRequired';
    const isDebitWarning = isDeliveryDelayed || isDeliveryFailed;
    const showContactUs = isWiresMigrationEnabled && contactUsFlag;
    const hasWarning = showContactUs || isVerification || isDebitWarning;
    // Cleans up the array to contain ONLY valid steps ( Ex: [{VerificationRequired},{Reviewing},{Requested}] -> [{Reviewing},{Requested}])
    const statusArray = statuses.filter(status => status.status !== 'VerificationRequired');
    const latestStatus = getStatus({
        activityName,
        currentStatus: statusArray[0].status,
    });
    const steps = () => {
        if (isCanceled || isDeliveryFailed)
            return getCanceledSteps({ activityName, statuses });
        if (isDeliveryDelayed)
            return trackerSteps.DebitCardOrderDelayed;
        return trackerSteps[activityName];
    };
    const ariaLabel = getAriaLabel({ event, steps: steps(), hasWarning });
    return (React.createElement(Box, { "aria-label": ariaLabel, role: "img" },
        React.createElement(TimelineTrackerSteps, { "aria-hidden": "true", isSummaryView: isSummaryView }, steps().map((step, index, timelineSteps) => {
            const historyCollection = statuses.find(history => {
                if (step === 'Ordered') {
                    return trackerStatuses.ordered.includes(history.status);
                }
                return history.status === step;
            });
            const stepStatus = getStepStatus({
                step,
                timelineSteps,
                latestStatus,
                currentStepIndex: index,
                hasWarning,
            });
            return isSummaryView ? (React.createElement(SummaryTrackerStep, { event: event, isCanceled: isCanceled, stepStatus: stepStatus, step: step })) : (React.createElement(TimelineTrackerStep, { event: event, stepStatus: stepStatus, statusHistory: historyCollection, step: step }));
        }))));
};
export default TimelineTracker;
//# sourceMappingURL=index.js.map